import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
  ReactNode,
  useMemo
} from 'react';
import { useAnimation, useMotionValue, useSpring } from 'framer-motion';
import { useIsMounted, useMediaQuery } from '@react-hookz/web';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth0User } from '@hooks/useAuth0User';
import { useIsAppView } from '@hooks/useIsAppView';
import { triggerToast } from '@components/atoms/toast';
import { getisAppView } from '@lib/utils';
import { FFH_EXTERNAL_ID, FFH_ROLES } from '@lib/constants';

export enum NotificationPreference {
  YES = 'yes',
  NO = 'no'
}

export enum ModalType {
  NONE = 'none',
  NO_UPDATES = 'no-updates',
  EMAIL_ONLY = 'email-only'
}

export type FormValues = {
  notificationPreference: NotificationPreference;
  hasEmailConsent?: boolean;
  hasPushConsent?: boolean;
};

interface AnimationControls {
  heading: ReturnType<typeof useAnimation>;
  text: ReturnType<typeof useAnimation>;
  bg: ReturnType<typeof useAnimation>;
  form: ReturnType<typeof useAnimation>;
}

export interface CheckboxOption {
  iconType: string;
  title: string;
  description: React.ReactNode;
  isChecked: boolean;
  fieldName: string;
  testId: string;
}

interface NotificationsFormReturn {
  formScope: React.RefObject<HTMLDivElement | null>;
  animationControls: AnimationControls;
  buttonScale: ReturnType<typeof useSpring>;
  isModalOpen: boolean;
  modalType: ModalType;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  handleConfirmModal: () => void;
  handleCloseModal: () => void;
  handleEnablePush: () => void;
  isSubmitting: boolean;
  isPending: boolean;
  error: string;
  isNoNotificationPreference: (values: FormValues) => boolean;
  isEmailOnlyPreference: (values: FormValues) => boolean;
  hasExistingPreferences: boolean;
  form: UseFormReturn<FormValues>;
  checkboxOptions: CheckboxOption[];
  createOptionProps: (
    value: NotificationPreference,
    name: string,
    emoji: string,
    title: string,
    description: string,
    extraInfo?: ReactNode,
    highlightColor?: 'green' | 'blue',
    testId?: string
  ) => {
    value: NotificationPreference;
    name: string;
    emoji: string;
    title: string;
    description: string;
    extraInfo?: ReactNode;
    highlightColor: 'green' | 'blue';
    testId: string;
  };
  yesOptionProps: {
    value: NotificationPreference;
    name: string;
    emoji: string;
    title: string;
    description: string;
    extraInfo?: ReactNode;
    highlightColor: 'green' | 'blue';
    testId: string;
  };
  noOptionProps: {
    value: NotificationPreference;
    name: string;
    emoji: string;
    title: string;
    description: string;
    extraInfo?: ReactNode;
    highlightColor: 'green' | 'blue';
    testId: string;
  };
}

export const createNotificationsSchema = (isAppView: boolean) =>
  z
    .object({
      notificationPreference: z.nativeEnum(NotificationPreference, {
        errorMap: () => ({ message: 'Please make a selection to continue' })
      }),
      hasEmailConsent: z.boolean().optional(),
      hasPushConsent: z.boolean().optional()
    })
    .refine(
      (data) => {
        if (
          data.notificationPreference === NotificationPreference.YES &&
          isAppView
        ) {
          return data.hasEmailConsent === true || data.hasPushConsent === true;
        }
        return true;
      },
      {
        message: 'Please select at least one notification method',
        path: ['consentOptions']
      }
    );

export type NotificationsSchema = ReturnType<typeof createNotificationsSchema>;

/**
 * Custom hook for managing notification preferences form
 */
const useNotificationPreferencesForm = (
  existingNotifications?: Record<string, any>
): NotificationsFormReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(ModalType.NONE);

  // Refs and utilities
  const formScope = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const hasExistingPreferences = !!existingNotifications;
  const { user } = useAuth0User();
  const router = useRouter();
  const isMounted = useIsMounted();
  const isMobile = useMediaQuery('(max-width: 640px)');

  // Animation setup
  const animationControls: AnimationControls = {
    heading: useAnimation(),
    text: useAnimation(),
    bg: useAnimation(),
    form: useAnimation()
  };

  const initialScale = useMotionValue(1);
  const buttonScale = useSpring(initialScale, {
    stiffness: isMobile ? 300 : 400,
    damping: isMobile ? 20 : 25
  });

  const getInitialFormValues = useCallback(() => {
    if (existingNotifications) {
      if (
        existingNotifications.hasEmailConsent ||
        existingNotifications.hasPushConsent
      ) {
        return {
          notificationPreference: NotificationPreference.YES,
          hasEmailConsent: !!existingNotifications.hasEmailConsent,
          hasPushConsent: !!existingNotifications.hasPushConsent
        };
      } else {
        return {
          notificationPreference: NotificationPreference.NO,
          hasEmailConsent: false,
          hasPushConsent: false
        };
      }
    } else {
      return {
        notificationPreference: undefined,
        hasEmailConsent: false,
        hasPushConsent: false
      };
    }
  }, [existingNotifications]);

  const { isAppView } = useIsAppView();

  const form = useForm<FormValues>({
    resolver: zodResolver(createNotificationsSchema(isAppView)),
    defaultValues: getInitialFormValues(),
    mode: 'onChange'
  });

  const { handleSubmit } = form;

  const prepareFormData = useCallback(
    (values: FormValues) => {
      const isAppView = getisAppView();
      return {
        email: user?.email,
        externalUserId: user?.[FFH_EXTERNAL_ID],
        name: user?.name,
        membershipTier: user?.[FFH_ROLES]?.[0]?.toLowerCase(),
        hasEmailConsent:
          values.notificationPreference === NotificationPreference.YES &&
          (!!values.hasEmailConsent || (!isAppView && !hasExistingPreferences)),
        hasPushConsent:
          values.notificationPreference === NotificationPreference.YES &&
          !!values.hasPushConsent
      };
    },
    [user, hasExistingPreferences]
  );

  const showToast = useCallback(
    (type: 'info' | 'success' | 'error', message: string) => {
      const toastConfig = {
        info: {
          variant: 'info',
          title: 'Saving preferences',
          subTitle: message
        },
        success: { variant: 'success', title: 'Success', subTitle: message },
        error: { variant: 'error', title: 'Error', subTitle: message }
      };

      triggerToast(toastConfig[type as any]);
    },
    []
  );

  const handleNavigation = useCallback(() => {
    if (!hasExistingPreferences) {
      setTimeout(() => {
        router.replace('/welcome');
      }, 1700);
    }
  }, [hasExistingPreferences, router]);

  const savePreferencesMutation = useMutation({
    mutationFn: async (formValues: FormValues): Promise<boolean> => {
      const data = prepareFormData(formValues);

      try {
        const response = await fetch('/api/notifications', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          const errorData = await response.json();
          const error = new Error(
            errorData.error || 'Failed to create/update preferences'
          );
          (error as any).status = response.status;
          (error as any).details = errorData.details || '';
          throw error;
        }

        return true;
      } catch (error) {
        if (!(error instanceof Error)) {
          throw new Error('Network error occurred');
        }
        throw error;
      }
    },
    onSuccess: async () => {
      if (isMounted()) {
        const externalUserId = user?.[FFH_EXTERNAL_ID]?.toString();
        if (externalUserId) {
          queryClient.invalidateQueries([
            'notificationPreferences',
            externalUserId
          ]);
        }

        showToast('success', 'Preferences saved successfully!');
        handleNavigation();
      }

      if (hasExistingPreferences) {
        setIsSubmitting(false);
        setIsPending(false);
      }
    },
    onError: (error) => {
      if (isMounted()) {
        console.error('Error saving preferences:', error);

        let errorMessage = 'Could not save preferences. Please try again.';

        if (error instanceof Error) {
          const status = (error as any).status;

          if (status === 401) {
            errorMessage = 'Your session has expired. Please log in again.';
          } else if (status === 403) {
            errorMessage =
              "You don't have permission to update these preferences.";
          } else if (status === 400) {
            errorMessage =
              'The form contains invalid data. Please check your inputs.';
          } else if (status === 500) {
            errorMessage = 'A server error occurred. Please try again later.';
          } else {
            const details = (error as any).details;
            if (details && typeof details === 'string') {
              errorMessage += ` ${details}`;
            } else {
              errorMessage += ` ${error.message}`;
            }
          }
        }

        showToast('error', errorMessage);
        setIsSubmitting(false);
        setIsPending(false);
      }
    }
  });

  const isNoNotificationPreference = useCallback(
    (values: FormValues): boolean =>
      values.notificationPreference === NotificationPreference.NO,
    []
  );

  const isEmailOnlyPreference = useCallback(
    (values: FormValues): boolean =>
      values.notificationPreference === NotificationPreference.YES &&
      values.hasEmailConsent === true &&
      !values.hasPushConsent,
    []
  );

  const processFormSubmission = useCallback(
    async (values: FormValues): Promise<void> => {
      if (!isMounted()) return;

      if (!user?.email || !user?.[FFH_EXTERNAL_ID]) {
        showToast('error', 'Your session is invalid. Please log in again.');
        return;
      }

      setIsSubmitting(true);
      showToast('info', 'Please wait...');

      if (!hasExistingPreferences) {
        setIsPending(true);
      }

      try {
        await savePreferencesMutation.mutateAsync(values);
      } catch (error) {
        console.error('Form submission failed:', error);
        setIsSubmitting(false);
        setIsPending(false);
      }
    },
    [
      savePreferencesMutation,
      isMounted,
      hasExistingPreferences,
      showToast,
      user
    ]
  );

  const onFormSubmit = useCallback(
    (values: FormValues) => {
      if (isNoNotificationPreference(values)) {
        setModalType(ModalType.NO_UPDATES);
        setIsModalOpen(true);
        return;
      }

      if (
        values.notificationPreference === NotificationPreference.YES &&
        values.hasEmailConsent === true &&
        !values.hasPushConsent &&
        (isAppView || hasExistingPreferences)
      ) {
        setModalType(ModalType.EMAIL_ONLY);
        setIsModalOpen(true);
        return;
      }

      processFormSubmission(values);
    },
    [
      processFormSubmission,
      isNoNotificationPreference,
      isEmailOnlyPreference,
      hasExistingPreferences
    ]
  );

  const submitHandler = handleSubmit(onFormSubmit);

  const handleCloseModal = useCallback((): void => {
    setIsModalOpen(false);
    setModalType(ModalType.NONE);
  }, []);

  const handleEnablePush = useCallback((): void => {
    setIsModalOpen(false);
    setModalType(ModalType.NONE);
    form.setValue('hasPushConsent', true);
    const values = form.getValues();
    processFormSubmission(values);
  }, [form, processFormSubmission]);

  const handleConfirmModal = useCallback((): void => {
    setIsModalOpen(false);
    setModalType(ModalType.NONE);
    const values = form.getValues();
    processFormSubmission(values);
  }, [form, processFormSubmission]);

  useEffect(() => {
    const animateFormElements = async (): Promise<void> => {
      const ANIMATION_DURATIONS = {
        heading: isMobile ? 0.4 : 0.5,
        text: isMobile ? 0.3 : 0.4,
        background: 0.5,
        form: isMobile ? 0.4 : 0.5
      };

      try {
        await animationControls.heading.start({
          opacity: 1,
          y: 0,
          transition: { duration: ANIMATION_DURATIONS.heading }
        });

        await animationControls.text.start({
          opacity: 1,
          y: 0,
          transition: { duration: ANIMATION_DURATIONS.text }
        });

        await animationControls.bg.start({
          opacity: 1,
          y: 0,
          transition: { duration: ANIMATION_DURATIONS.background }
        });

        await animationControls.form.start({
          opacity: 1,
          transition: { duration: ANIMATION_DURATIONS.form }
        });
      } catch (error) {
        console.error('Animation sequence error:', error);
      }
    };

    animateFormElements();
  }, [animationControls, isMobile]);

  const createOptionProps = useCallback(
    (
      value: NotificationPreference,
      name: string,
      emoji: string,
      title: string,
      description: string,
      extraInfo?: ReactNode,
      highlightColor: 'green' | 'blue' = 'green',
      testId?: string
    ) => ({
      value,
      name,
      emoji,
      title,
      description,
      extraInfo,
      highlightColor,
      testId: testId || `marketing-${value}`
    }),
    []
  );

  const hasEmailConsent = form.watch('hasEmailConsent') || false;
  const hasPushConsent = form.watch('hasPushConsent') || false;

  const yesOptionProps = useMemo(
    () =>
      createOptionProps(
        NotificationPreference.YES,
        'notificationPreference',
        '🏆',
        'Yes, unlock my winning edge! ✓',
        isAppView || hasExistingPreferences
          ? 'Get exclusive player insights, price change alerts, and critical injury updates — delivered straight to your inbox/device'
          : 'Get exclusive player insights, price change alerts, and critical injury updates — delivered straight to your inbox',
        undefined,
        'green',
        'marketing-yes'
      ),
    [isAppView, hasExistingPreferences, createOptionProps]
  );

  const noOptionProps = useMemo(
    () =>
      createOptionProps(
        NotificationPreference.NO,
        'notificationPreference',
        '🤔',
        "No thanks, I'll manage my team on my own",
        `You won't receive winning insights, but you can update your preferences anytime${
          hasExistingPreferences ? '' : ' in your account settings'
        }`,
        undefined,
        'blue',
        'marketing-no'
      ),
    [hasExistingPreferences, createOptionProps]
  );

  const checkboxOptions = useMemo(
    () => [
      {
        iconType: 'email',
        title: 'Email updates',
        description: 'Weekly expert picks, deadline reminders & injury alerts',
        isChecked: hasEmailConsent,
        fieldName: 'hasEmailConsent',
        testId: 'email-consent'
      },
      {
        iconType: 'push',
        title: 'Push notifications',
        description: (
          <>
            Instant updates <em>the moment</em> news breaks — perfect for
            staying ahead of your rivals
          </>
        ),
        isChecked: hasPushConsent,
        fieldName: 'hasPushConsent',
        testId: 'push-consent'
      }
    ],
    [hasEmailConsent, hasPushConsent]
  );

  return {
    formScope,
    animationControls,
    buttonScale,
    isModalOpen,
    modalType,
    handleSubmit: submitHandler,
    handleConfirmModal,
    handleCloseModal,
    handleEnablePush,
    isSubmitting,
    isPending,
    error: savePreferencesMutation.error
      ? (savePreferencesMutation.error as Error).message
      : '',
    isNoNotificationPreference,
    isEmailOnlyPreference,
    hasExistingPreferences,
    form,
    checkboxOptions,
    createOptionProps,
    yesOptionProps,
    noOptionProps
  };
};

export default useNotificationPreferencesForm;
